import { PropsWithChildren, useCallback, useState, useEffect, useRef } from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SearchFilterMenuItemType } from "../FilterMenu.model";
import { LikeFilter } from "../FieldFilter.model";
import styles from "../FilterMenu.module.scss";

interface SearchMenuProps {
  placeholder: string;
  value: string;
  onSubmit: (value: string) => void;
}

function SearchMenu({
  children,
  placeholder,
  value,
  onSubmit,
}: PropsWithChildren<SearchMenuProps>) {
  const [search, setSearch] = useState(value);
  const hasChangedRef = useRef(false);
  const initialValueRef = useRef(value);

  useEffect(() => {
    setSearch(value);
    initialValueRef.current = value;
    hasChangedRef.current = false;
  }, [value]);

  const handleSubmit = useCallback((forceSubmit = false) => {
    if (!forceSubmit && !hasChangedRef.current && search === initialValueRef.current) {
      return;
    }
    onSubmit(search);
    hasChangedRef.current = false;
  }, [search, onSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    hasChangedRef.current = true;
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[240px]">
        <Input
          placeholder={placeholder}
          value={search}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit(true);
            }
          }}
          onBlur={() => handleSubmit(false)}
          className="w-[214px] h-6"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface SearchActiveFilterItemProps<Field extends string>
  extends SearchFilterMenuItemType<Field> {
  value: LikeFilter;
  size: "1" | "2" | "3";
  viewOnly: boolean;
  onChange: (item: SearchFilterMenuItemType<Field>) => void;
}

export function SearchActiveFilterItem<Field extends string>({
  size,
  viewOnly,
  onChange,
  ...item
}: SearchActiveFilterItemProps<Field>) {
  const displayValue = item.value?.value.replaceAll("%", "") || item.defaultValue || "";

  const handleSubmit = useCallback(
    (value: string) => {
      if (value === "") {
        onChange({ ...item, value: undefined });
        return;
      }

      onChange({
        ...item,
        value: { operator: "like", value: `%${value}%` },
      });
    },
    [item, onChange]
  );

  const handleClear = useCallback(
    () => onChange({ ...item, value: undefined }),
    [item, onChange]
  );

  return (
    <Flex gap={"1px"} className={styles.activeFilter} align="center">
      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        {item.icon}
        {item.label}
      </div>

      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        like
      </div>

      <SearchMenu
        placeholder={item.label}
        value={displayValue}
        onSubmit={handleSubmit}
      >
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
            viewOnly ? "pointer-events-none" : ""
          } shrink`}
        >
          <Flex align="center" gap="2">
            <Text truncate>{displayValue}</Text>
          </Flex>
        </div>
      </SearchMenu>

      {!viewOnly && (
        <IconButton
          variant="soft"
          color="gray"
          size={size}
          onClick={handleClear}
        >
          <Cross1Icon />
        </IconButton>
      )}
    </Flex>
  );
}
